/* eslint-disable require-atomic-updates */
import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import i18n from 'src/lib/i18n/i18n';
import { Feature } from 'src/replicant/analytics';
import StateObserver, { now, user } from 'src/StateObserver';
import Scroll from 'src/game/components/shared/Scroll';
import uiConfig from 'src/lib/ui/config';
import PopupMatch2Base, {
  PopupMatch2BaseInitOptions,
} from '../../components/PopupMatch2Base';
import { RacesEntryParams, RacesEntry } from '../components/RacesEntry';
import { RacesTimer } from '../components/RacesTimer';
import {
  getActiveRace,
  getRaceOpponents,
  getRaceRewards,
  getRaceStartTime,
} from 'src/replicant/match2/races/races.getters';
import { getRaceTheme, showFakeRaceLoadSpinner } from '../races.utils';
import { TimedRaceConfig } from 'src/replicant/match2/races/races.ruleset';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { engine } from '@play-co/timestep-core';
import { GENERIC_AVATAR } from 'src/replicant/profile/assets';

interface PopupMatch2TimedRacesLeaderboardInitOptions extends PopupMatch2BaseInitOptions {
  raceId?: string;
  raceIndex?: number;
}

export default class PopupMatch2TimedRacesLeaderboard extends PopupMatch2Base {
  private readonly scroll: Scroll<RacesEntryParams, RacesEntry>;
  private mainPlayer?: RacesEntry;
  private readonly timer: RacesTimer;
  private readonly scrollArea: ImageScaleView;
  private config!: TimedRaceConfig;
  private banner: ImageScaleView;
  private infoButton: ButtonScaleView;
  private infoIcon: ImageScaleView;
  private tickHandler: () => void = () => void 0;
  private playerRank?: number;

  constructor(opts: { superview: View; close: (success: boolean) => void }) {
    const boxWidth = 646;
    const boxHeight = 539;

    super({
      superview: opts.superview,
      width: boxWidth,
      height: boxHeight,
      panelImageOpts: {
        ...uiConfig.popups.buruburu,
        width: boxWidth,
        height: boxHeight,
      },
      offsetY: -175,
      close: () => opts.close(false),
      title: { type: 'image' },
      cta: {
        localeText: () => i18n('match2.tournament.claim').toLocaleUpperCase(),
        onClick: async () => {
          opts.close(true);
        },
        positioning: 'edge',
        offsetY: 25,
      },
      tracking: {
        feature: Feature.MATCH2.TOURNAMENT._,
        $subFeature: Feature.MATCH2.TOURNAMENT.POPUP,
      },
    });

    this.banner = new ImageScaleView({
      superview: this.panel.getSuperview()!,
      infinite: true,
      canHandleEvents: false,
    });

    this.buttonClose!.updateOpts({ superview: this.banner });

    this.title?.updateOpts({
      superview: this.banner,
    });

    this.panel.updateOpts({
      y: 335,
    });

    this.scrollArea = new ImageScaleView({
      superview: this.panel,
      image: 'assets/ui/match2/common/list_scroll_area.png',
      width: boxWidth - 40,
      height: boxHeight - 80,
      x: 20,
      y: 40,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: {
          left: 22,
          right: 22,
        },
        vertical: {
          top: 22,
          bottom: 22,
        },
      },
    });

    const button = new ButtonScaleView({
      superview: this.banner,
      width: 225,
      height: 52,
      x: boxWidth * 0.5 + 11,
      y: 307,
      pressedOffsetY: 5,
      centerOnOrigin: true,
      tracking: {
        feature: Feature.MATCH2.RACES._,
        $subFeature: Feature.MATCH2.RACES.LEADERBOARD_INFO,
      },
      onClick: async () => {
        await openPopupPromise('popupMatch2RacesIntro', {
          useClickOutside: true,
          info: true,
          config: this.config,
        });
      },
    });
    this.infoButton = button;

    this.timer = new RacesTimer({
      superview: button,
    });

    this.infoIcon = new ImageScaleView({
      superview: button,
      image: `assets/ui/match2/races/common/btn_info.png`,
      centerOnOrigin: true,
      x: button.style.width - 23,
      y: button.style.height * 0.5,
      width: 45,
      height: 45,
    });

    this.scroll = new Scroll({
      superview: this.panel,
      createItem: this.createItem.bind(this),
      recycle: true,
      setData: this.setItemData.bind(this),
      rect: {
        x: this.scrollArea.style.x + 22,
        y: this.scrollArea.style.y + 5,
        width: this.scrollArea.style.width - 10,
        height: this.scrollArea.style.height - 10,
      },
    });
  }

  public init({
    raceId,
    raceIndex,
    useClickOutside,
  }: PopupMatch2TimedRacesLeaderboardInitOptions) {
    super.init({ useClickOutside });

    const previousRace = !!raceId;
    raceId = raceId ?? getActiveRace(now())?.id;

    const races = user().match2.races[raceId!];
    raceIndex = raceIndex ?? (races?.length ?? 1) - 1;

    // Claim mode.
    if (previousRace) {
      this.buttonCta!.show();
      const hasRewards =
        getRaceRewards(user(), raceId!, raceIndex!, now()).length > 0;
      const buttonText = hasRewards ? `match2.tournament.claim` : 'basic.ok';
      this.buttonCta!.localeText = () => i18n(buttonText).toLocaleUpperCase();
      this.buttonClose?.hide();
      this.timer.setFinished(true);
      this.infoButton.setDisabled(true);
      this.infoIcon.hide();
    } else {
      this.buttonCta?.hide();
      this.buttonClose?.show();
      this.timer.setFinished(false);
      this.infoButton.setDisabled(false);
      this.infoIcon.show();
    }

    const raceConfig = getActiveRace(getRaceStartTime(raceId!))
      ?.config as TimedRaceConfig;
    if (!raceConfig) return;

    this.config = raceConfig;

    this.setTitleImage({
      ...this.getPopupTitle(),
      centerOnOrigin: true,
      scaleMethod: 'contain',
    });

    this.banner.updateOpts({
      ...this.getPopupBanner(),
    });

    const player = {
      name:
        user().publicProfile?.name ??
        user().profile?.name ??
        i18n('match2.tournament.you'),
      avatar:
        user().publicProfile?.photo ?? user().profile?.photo ?? GENERIC_AVATAR,
      score: races[raceIndex!]?.score ?? 0,
      me: true,
    };

    this.mainPlayer = new RacesEntry(
      {
        superview: this.panel,
        width: this.scrollArea.style.width + 14,
        height: 100,
        x: this.scrollArea.style.x - 7,
        y: this.scrollArea.style.y + this.scrollArea.style.height - 90,
      },
      raceConfig.type,
      getRaceTheme(this.config),
    );

    const allPlayers = [
      ...getRaceOpponents(user(), raceId!, raceIndex!, now()),
      player,
    ];
    allPlayers.sort((a, b) => b.score - a.score);
    this.playerRank = allPlayers.indexOf(player) + 1;
    this.mainPlayer.setup({
      ...player,
      position: this.playerRank,
      me: false,
      alt: true,
    });
    this.scroll.setItems(
      allPlayers.map((x, index) => ({
        ...x,
        position: index + 1,
      })),
    );

    this.scroll.getView().hide();
  }

  private createItem(superview: View, i: number, data: RacesEntryParams) {
    const entry = new RacesEntry(
      { superview },
      this.config.type,
      getRaceTheme(this.config),
    );
    entry.setup({ ...data, position: i + 1 });
    return entry;
  }

  private setItemData(i: number, data: RacesEntryParams, view: RacesEntry) {
    view.setup({ ...data, position: i + 1 });
  }

  public override async fadeIn(): Promise<void> {
    this.tickHandler = this.tick.bind(this);
    engine.addListener('Tick', this.tickHandler);
    await super.fadeIn();
    await showFakeRaceLoadSpinner(this.panel);
    this.scroll.getView().show();
  }

  public override async fadeOut(): Promise<void> {
    engine.removeListener('Tick', this.tickHandler);
    return super.fadeOut();
  }

  private tick() {
    // Not properly initialized yet, probably the async getter isn't ready
    if (this.playerRank == null) {
      this.mainPlayer?.hide();
      return;
    }

    if (this.scroll.isItemVisible(this.playerRank)) {
      this.mainPlayer?.hide();
      return;
    }

    const top =
      this.scroll.getElementPosition(this.playerRank) <
      this.scroll.viewportUpperBounds;

    this.mainPlayer?.updateOpts({
      visible: true,
      y: top
        ? 32
        : this.scrollArea.style.y + this.scrollArea.style.height - 100,
    });
  }

  private getPopupBanner() {
    return getRaceTheme(this.config).leaderboard.background;
  }

  private getPopupTitle() {
    const locale = StateObserver.getLocale() === 'en' ? 'en' : 'jp';
    return getRaceTheme(this.config).title[locale];
  }
}
